import { useState } from "react";
import {
  AuthShell,
  AuthAlert,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from "@/components/auth/auth-shell";
import { LocaleLink } from "@/components/locale-link";
import { Button } from "@/components/ui/button";
import { useRegister } from "@/hooks/use-tp-backend-auth";
import { useTranslation } from "@/common/providers/i18n-provider";
import { localePathPrefix } from "@/lib/i18n";

interface SignUpPageProps {
  nextPath?: string;
}

/**
 * Registration form, ported from tianpan-v3-web's
 * `src/pages/auth/sign-up.tsx`. `register` doesn't return a token (tp-backend
 * requires email verification first) — on success this hands off to
 * `/auth/verify-email?email=…`, mirroring the source page exactly.
 */
export function SignUpPage({ nextPath = "/" }: SignUpPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { t, locale } = useTranslation();
  const localePrefix = localePathPrefix(locale);

  const { register, loading, error } = useRegister();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (password !== confirmPassword) {
      setErrorMessage(t("auth.error.passwordsMismatch"));
      return;
    }
    if (!name.trim()) {
      setErrorMessage(t("auth.signUp.nameRequired"));
      return;
    }

    try {
      const result = await register({ variables: { email, password, name } });
      const registrationData = result.data?.register;

      if (registrationData?.success) {
        window.location.href = `${localePrefix}/auth/verify-email?email=${encodeURIComponent(email)}&next=${encodeURIComponent(nextPath)}`;
      } else {
        setErrorMessage(
          registrationData?.message || t("auth.signUp.failed"),
        );
      }
    } catch {
      setErrorMessage(t("auth.signUp.failed"));
    }
  };

  return (
    <AuthShell
      eyebrow={t("auth.signUp.eyebrow")}
      title={t("auth.signUp.title")}
      subtitle={t("auth.signUp.subtitle")}
      footer={
        <LocaleLink to="/auth/login" className="text-primary hover:underline">
          {t("auth.signUp.toSignIn")}
        </LocaleLink>
      }
    >
      <AuthAlert message={error?.message || errorMessage} />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className={AUTH_LABEL_CLASS}>
            {t("auth.field.name")}
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
            placeholder={t("auth.field.namePlaceholder")}
            className={AUTH_INPUT_CLASS}
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="email" className={AUTH_LABEL_CLASS}>
            {t("auth.field.email")}
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={AUTH_INPUT_CLASS}
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="password" className={AUTH_LABEL_CLASS}>
            {t("auth.field.password")}
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            placeholder="••••••••"
            className={AUTH_INPUT_CLASS}
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className={AUTH_LABEL_CLASS}>
            {t("auth.field.confirmPassword")}
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            placeholder="••••••••"
            className={AUTH_INPUT_CLASS}
            disabled={loading}
          />
        </div>

        <Button type="submit" disabled={loading} className="w-full" size="lg">
          {loading ? t("auth.signUp.submitting") : t("auth.signUp.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
